'use client';

import { ReactNode, useState, Children, isValidElement } from 'react';

interface TabProps {
  label: string;
  children: ReactNode;
}

export function Tab({ children }: TabProps) {
  return <>{children}</>;
}

interface TabsProps {
  children: ReactNode;
  defaultTab?: number;
}

export function Tabs({ children, defaultTab = 0 }: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const tabs = Children.toArray(children).filter((child) =>
    isValidElement(child) && child.type === Tab
  );

  return (
    <div className="my-8">
      {/* Tab Headers */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {tabs.map((tab, index) => {
          if (!isValidElement<TabProps>(tab)) return null;
          const label = tab.props.label;
          const isActive = activeTab === index;

          return (
            <button
              type="button"
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab(index);
              }}
              className={`relative px-4 py-3 font-medium text-sm whitespace-nowrap transition-all ${
                isActive
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {label}
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400"></span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {tabs.map((tab, index) => {
          if (!isValidElement<TabProps>(tab)) return null;
          const isActive = activeTab === index;

          return (
            <div
              key={index}
              className={isActive ? 'block' : 'hidden'}
              role="tabpanel"
            >
              {tab.props.children}
            </div>
          );
        })}
      </div>
    </div>
  );
}
